<script setup lang="ts">
import { computed } from 'vue'
import type { InputToggle } from '../types'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useCardContext } from '../composables/useCardContext'
import { spacingClasses } from '../utils/classes'

const props = defineProps<{ element: InputToggle }>()

const { inputs, errors, cardInstanceId, isEnabled } = useCardContext()
const inputReadOnly = computed(() => !isEnabled.value || props.element.isEnabled === false)

const valueOn = computed(() => props.element.valueOn ?? 'true')
const valueOff = computed(() => props.element.valueOff ?? 'false')

const isChecked = computed(() => {
  const current = inputs[props.element.id] ?? props.element.value ?? valueOff.value
  return current === valueOn.value
})

function handleChange(checked: boolean) {
  inputs[props.element.id] = checked ? valueOn.value : valueOff.value
  if (errors[props.element.id]) {
    errors[props.element.id] = ''
  }
}

function normalizeDomToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_')
}

const fieldInputId = computed(() => `${cardInstanceId}-${normalizeDomToken(props.element.id)}`)
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5', spacingClasses[props.element.spacing ?? 'default'])">
    <Label v-if="props.element.label">
      {{ props.element.label }}
      <span v-if="props.element.isRequired" class="text-destructive">*</span>
    </Label>
    <div class="flex items-center gap-2">
      <Checkbox
        :id="fieldInputId"
        :disabled="inputReadOnly"
        :checked="isChecked"
        :class="cn(errors[props.element.id] && 'border-destructive')"
        @update:checked="handleChange"
      />
      <Label :for="fieldInputId" class="font-normal cursor-pointer">
        {{ props.element.title }}
      </Label>
    </div>
    <p v-if="errors[props.element.id]" class="text-sm text-destructive">
      {{ errors[props.element.id] }}
    </p>
  </div>
</template>
